/**
 * 日次【集計】の色の設定(2026-09-24 発注者指示「日次集計の色付けも設定画面で設定できるように」。P-61)。
 * 保存先は `settings/staff_summary`(管理者だけが書ける既存の settings コレクション。ルールの変更なし)。
 *  - 配色は**現行スプレッドシートと同じ塗りつぶしに固定**(2026-09-24 発注者指示。切替はやめた)
 *  - しきい値は現行シートの条件付き書式の値が既定。管理者が設定画面から変えられる
 */
import { doc, getDoc, serverTimestamp, setDoc } from 'firebase/firestore';
import { getDb } from '@core/lib/firebase';

export type StaffSummaryDisplay = {
  /** 時間別取得件数: この件数以上を赤で塗る(シートは3件) */
  hour_red: number;
  /** 取得率(%): これ以上で薄橙(シートは5%) */
  hit_rate_good: number;
  /** 1取得あたりの通話時間(分): これ以下で薄橙(シートは15分) */
  talk_per_appt_good_min: number;
  /** 1取得あたりの稼働時間(分): これ以下で薄橙(シートは24分) */
  work_per_appt_good_min: number;
  /** 1時間あたりの通話回数: これ以上で薄橙(シートは80回) */
  calls_per_hour_good: number;
  /** 取得実績(件/h): これより多いと青(給湯器2.2 / 不用品0.4) */
  apph_hi: number;
  apph_hi_fuyohin: number;
  /** 取得実績(件/h): これ未満だと赤(給湯器1.6 / 不用品は0.2以下) */
  apph_lo: number;
  apph_lo_fuyohin: number;
};

/** 現行スプレッドシート「日次【集計】⑨」の条件付き書式の値 */
export const DEFAULT_DISPLAY: StaffSummaryDisplay = {
  hour_red: 3,
  hit_rate_good: 5,
  talk_per_appt_good_min: 15,
  work_per_appt_good_min: 24,
  calls_per_hour_good: 80,
  apph_hi: 2.2,
  apph_hi_fuyohin: 0.4,
  apph_lo: 1.6,
  apph_lo_fuyohin: 0.2,
};

const PATH = ['settings', 'staff_summary'] as const;
const num = (v: unknown, fallback: number) => (Number.isFinite(Number(v)) && Number(v) >= 0 ? Number(v) : fallback);

export function normalizeDisplay(data: Record<string, unknown> | undefined): StaffSummaryDisplay {
  const d = data ?? {};
  return {
    hour_red: num(d.hour_red, DEFAULT_DISPLAY.hour_red),
    hit_rate_good: num(d.hit_rate_good, DEFAULT_DISPLAY.hit_rate_good),
    talk_per_appt_good_min: num(d.talk_per_appt_good_min, DEFAULT_DISPLAY.talk_per_appt_good_min),
    work_per_appt_good_min: num(d.work_per_appt_good_min, DEFAULT_DISPLAY.work_per_appt_good_min),
    calls_per_hour_good: num(d.calls_per_hour_good, DEFAULT_DISPLAY.calls_per_hour_good),
    apph_hi: num(d.apph_hi, DEFAULT_DISPLAY.apph_hi),
    apph_hi_fuyohin: num(d.apph_hi_fuyohin, DEFAULT_DISPLAY.apph_hi_fuyohin),
    apph_lo: num(d.apph_lo, DEFAULT_DISPLAY.apph_lo),
    apph_lo_fuyohin: num(d.apph_lo_fuyohin, DEFAULT_DISPLAY.apph_lo_fuyohin),
  };
}

export async function fetchStaffSummaryDisplay(): Promise<StaffSummaryDisplay> {
  const snap = await getDoc(doc(getDb(), ...PATH));
  return normalizeDisplay(snap.data());
}

export async function saveStaffSummaryDisplay(v: StaffSummaryDisplay, userId: string): Promise<void> {
  await setDoc(doc(getDb(), ...PATH), { ...v, updated_by: userId, updated_at: serverTimestamp() }, { merge: true });
}
